import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import { AdminLayout, btn, btnGhost, inp, lbl } from "@/pages/admin/adminShared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = { name: "", address: "", town: "", postcode: "", parking: "", accessibility: "", notes: "" };

export default function ManageVenues() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const { data: venues, isLoading } = useQuery({ queryKey: ["venues"], queryFn: async () => (await api.get("/venues")).data });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const openNew = () => { setF(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (v) => { setF(v); setEditId(v.id); setOpen(true); };
  const save = async () => {
    try {
      if (editId) await api.put(`/admin/venues/${editId}`, f); else await api.post("/admin/venues", f);
      qc.invalidateQueries({ queryKey: ["venues"] }); setOpen(false); toast.success("Venue saved");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const del = async (v) => { await api.delete(`/admin/venues/${v.id}`); qc.invalidateQueries({ queryKey: ["venues"] }); toast("Venue deleted"); };

  return (
    <AdminLayout role={user?.role} title="Venues" subtitle="Reusable venues — classes and events reference these."
      actions={<button className={btn} onClick={openNew} data-testid="add-venue-button"><Plus size={16} /> Add venue</button>}>
      {isLoading ? <Spinner /> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {venues.map((v) => (
            <div key={v.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`venue-row-${v.slug}`}>
              <div className="flex items-start justify-between">
                <h3 className="font-display font-bold text-[#2D1E18]">{v.name}</h3>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(v)} className="rounded-lg p-1.5 text-[#5C4A42] hover:bg-secondary" aria-label="Edit"><Pencil size={15} /></button>
                  <button onClick={() => del(v)} className="rounded-lg p-1.5 text-[#6E5B51] hover:text-[#991B1B]" aria-label="Delete"><Trash2 size={15} /></button>
                </div>
              </div>
              <p className="mt-1 text-sm text-[#5C4A42]">{v.address}{v.town ? `, ${v.town}` : ""} {v.postcode}</p>
              {v.parking && <p className="mt-2 text-sm text-[#6E5B51]">🅿 {v.parking}</p>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Edit venue" : "Add venue"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className={lbl}>Name</label><input className={inp} value={f.name} onChange={set("name")} data-testid="venue-form-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Address</label><input className={inp} value={f.address} onChange={set("address")} /></div>
              <div><label className={lbl}>Town</label><input className={inp} value={f.town} onChange={set("town")} /></div>
              <div><label className={lbl}>Postcode</label><input className={inp} value={f.postcode} onChange={set("postcode")} /></div>
            </div>
            <div><label className={lbl}>Parking</label><input className={inp} value={f.parking} onChange={set("parking")} /></div>
            <div><label className={lbl}>Accessibility</label><input className={inp} value={f.accessibility} onChange={set("accessibility")} /></div>
          </div>
          <DialogFooter><button className={btnGhost} onClick={() => setOpen(false)}>Cancel</button><button className={btn} onClick={save} data-testid="venue-form-save">Save</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
